import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Table, Badge, Alert,
    Button, Modal, Form, ListGroup, Accordion
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { adminInstance } from '../../services/axiosInstance';
import LoaderOverlay from '../../Components/shared/Loader';
import formatDateForInput from '../../services/CommonServices/dateService';

// Mock API Service for View Details
const viewService = {
    getRequestDetails: async (id) => {
        // Mock data matching your backend structure
        return {
            ID: parseInt(id),
            REQ_DATE: '2024-01-15T10:30:00',
            EMP_ID: 101,
            CUS_CODE: 'CUST001',
            CUS_NAME: 'John Doe',
            CUS_ADDRESS: '123 Main St, City',
            ITEM_CODE: 'PROD001',
            ITEM_NAME: 'AC Unit Pro',
            MODEL: 'AC-PRO-2024',
            SERIAL_NO: 'SN-AC001-2024',
            INSTALLATION_DATE: '2023-12-15',
            NOTE: 'AC not cooling properly, needs service',
            REQ_STATUS: 'PENDING_APPROVAL', // PENDING_APPROVAL, APPROVED, REJECTED, COMPLETED
            TOTAL_AMOUNT: 164.98,
            REQ_FILES: ['photo1.jpg', 'service_report.pdf'],
            PO_REFERENCE: 'PO-2024-001',
            MS_SERVICE_REQUEST_ITEM: [
                {
                    ID: 1,
                    ITEM_CODE: 'SPARE001',
                    ITEM_DESC: 'Fan Motor 1200 RPM',
                    UNIT_PRICE: 89.99,
                    QTY: 1,
                    AMOUNT: 89.99
                },
                {
                    ID: 2,
                    ITEM_CODE: 'SPARE002',
                    ITEM_DESC: 'AC Filter Premium',
                    UNIT_PRICE: 24.99,
                    QTY: 3,
                    AMOUNT: 74.99
                }
            ],
            APPROVALS: [
                {
                    ID: 1,
                    ACTION_BY: 'Manager 1',
                    ACTION_DATE: '2024-01-16T09:15:00',
                    ACTION: 'APPROVED',
                    COMMENTS: 'Looks good, proceed with service',
                    ROLE: 'Technical Manager'
                },
                {
                    ID: 2,
                    ACTION_BY: 'Finance Officer',
                    ACTION_DATE: '2024-01-16T14:30:00',
                    ACTION: 'PENDING',
                    COMMENTS: 'Waiting for budget approval',
                    ROLE: 'Finance'
                },
                {
                    ID: 3,
                    ACTION_BY: 'Service Head',
                    ACTION_DATE: null,
                    ACTION: 'PENDING',
                    COMMENTS: '',
                    ROLE: 'Service Department'
                },
                {
                    ID: 4,
                    ACTION_BY: 'Final Approver',
                    ACTION_DATE: null,
                    ACTION: 'PENDING',
                    COMMENTS: '',
                    ROLE: 'Final Approval'
                }
            ]
        };
    },

    getApproversList: async () => {
        return [
            { id: 1, name: 'Manager 1', role: 'Technical Manager', email: 'manager1@company.com' },
            { id: 2, name: 'Finance Officer', role: 'Finance', email: 'finance@company.com' },
            { id: 3, name: 'Service Head', role: 'Service Department', email: 'service@company.com' },
            { id: 4, name: 'Final Approver', role: 'Final Approval', email: 'final@company.com' }
        ];
    },

    submitApproval: async (requestId, approvalData) => {
        console.log('Submitting approval:', { requestId, approvalData });
        return { success: true };
    }
};

// Service type mapping
const SERVICE_TYPES = {
    DEMO: { label: 'Demo Request', color: 'primary' },
    WARRANTY: { label: 'Warranty Request', color: 'success' },
    SHORT_SUPPLY: { label: 'Short Supply', color: 'warning' },
    WRONG_SUPPLY: { label: 'Wrong Supply', color: 'danger' },
    FOC_GOODWILL: { label: 'FOC-Goodwill', color: 'secondary' },
    SERVICE: { label: 'Service Invoice Request', color: 'info' }
};

// Status mapping with colors
const STATUS_CONFIG = {
    Open: { label: 'Open', color: 'warning' },
    "Pending with Manager": { label: 'Pending with Manager', color: 'warning' },
    "Pending with MD": { label: 'Pending with MD', color: 'warning' },
    "Pending with Commercial": { label: 'Pending with Commercial', color: 'warning' },
    Completed: { label: 'Completed', color: 'success' }
};



// Approver Action Modal
// Approver Action Modal - Alternative using custom radio buttons
const ApproverActionModal = ({ show, onHide, onSubmit, requestId, currentApprover, reqStatus, SoNo, InvNo, InvDate, CourierName, LrNumber, CommercialComment }) => {
    const IsCommercialApproval = () => {
        if (currentApprover.includes('Commercial') && reqStatus === 'Pending with Commercial')
            return true;
        else return false;
    }

    const initialValues = {
        ACTION: '',
        COMMENTS: IsCommercialApproval() ? CommercialComment : '',
        SO_NUMBER: SoNo,
        INVOICE_NUMBER: InvNo,
        INVOICE_DATE: InvDate ? formatDateForInput(InvDate) : '', // new Date(InvDate).toISOString().split('T')[0],
        COURIER_NAME: CourierName,
        LR_NUMBER: LrNumber
    };

    const validate = (values) => {
        const errors = {};

        if (!values.ACTION) {
            errors.ACTION = 'Action is required';
        }

        // Comments validation
        if ((values.ACTION === 'REJECTED' || values.ACTION === 'PENDING') && !values.COMMENTS?.trim()) {
            errors.COMMENTS = 'Comments are required';
        }

        // Commercial user validation for approved status
        if (currentApprover === 'Commercial' && values.ACTION === 'APPROVED') {
            if (!values.SO_NUMBER?.trim()) {
                errors.SO_NUMBER = 'SO Number is required';
            }
            if (!values.INVOICE_NUMBER?.trim()) {
                errors.INVOICE_NUMBER = 'Invoice Number is required';
            }
            if (!values.INVOICE_DATE) {
                errors.INVOICE_DATE = 'Invoice Date is required';
            }
            if (!values.COURIER_NAME?.trim()) {
                errors.COURIER_NAME = 'Courier Name is required';
            }
            if (!values.LR_NUMBER?.trim()) {
                errors.LR_NUMBER = 'LR Number is required';
            }
        }

        return errors;
    };

    const handleSubmit = (values, { setSubmitting }) => {
        const approvalData = {
            Action: values.ACTION,
            ApproverComment: values.COMMENTS,
            RequestId: requestId
        };

        // Add commercial details if applicable
        if (IsCommercialApproval()) {
            approvalData.SoNo = values.SO_NUMBER;
            approvalData.InvNo = values.INVOICE_NUMBER;
            approvalData.InvDate = values.INVOICE_DATE;
            approvalData.CourierName = values.COURIER_NAME;
            approvalData.LrNumber = values.LR_NUMBER;
        }

        onSubmit(approvalData);
        setSubmitting(false);
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Take Action</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validate={validate}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleSubmit, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            {/* Action Selection */}
                            <Form.Group className="mb-3">
                                <Form.Label>Action *</Form.Label>
                                <div role="group" aria-labelledby="my-radio-group">
                                    <Field name="ACTION">
                                        {({ field }) => (
                                            <div className="d-flex flex-column gap-2">
                                                <div className="form-check">
                                                    <input
                                                        {...field}
                                                        type="radio"
                                                        id="APPROVE"
                                                        value="APPROVED"
                                                        checked={field.value === 'APPROVED'}
                                                        className="form-check-input"
                                                    />
                                                    <label htmlFor="APPROVE" className="form-check-label">
                                                        Approve
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        {...field}
                                                        type="radio"
                                                        id="REJECT"
                                                        value="REJECTED"
                                                        checked={field.value === 'REJECTED'}
                                                        className="form-check-input"
                                                    />
                                                    <label htmlFor="REJECT" className="form-check-label">
                                                        Reject
                                                    </label>
                                                </div>
                                                {IsCommercialApproval() && (
                                                    <div className="form-check">
                                                        <input
                                                            {...field}
                                                            type="radio"
                                                            id="PENDING"
                                                            value="PENDING"
                                                            checked={field.value === 'PENDING'}
                                                            className="form-check-input"
                                                        />
                                                        <label htmlFor="PENDING" className="form-check-label">
                                                            Pending
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Field>
                                </div>
                                {errors.ACTION && touched.ACTION && (
                                    <div className="text-danger mt-1">{errors.ACTION}</div>
                                )}
                            </Form.Group>

                            {/* Comments Section */}
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Comments {values.ACTION === 'REJECTED' && '(Required)'} 
                                </Form.Label>
                                <Field
                                    as="textarea"
                                    className="form-control"
                                    name="COMMENTS"
                                    rows={3}
                                    placeholder="Enter your comments..."
                                />
                                {errors.COMMENTS && touched.COMMENTS && (
                                    <div className="text-danger mt-1">{errors.COMMENTS}</div>
                                )}
                            </Form.Group>

                            {/* Commercial User Fields - Only shown for Commercial user when Approving */}
                            {IsCommercialApproval() && (
                                <Card className="mb-3 border-primary">
                                    <Card.Header className="bg-primary text-white">
                                        <h6 className="mb-0">Commercial Details *</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>SO Number *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="text"
                                                        name="SO_NUMBER"
                                                        placeholder="Enter SO Number"
                                                        isInvalid={touched.SO_NUMBER && errors.SO_NUMBER}
                                                    />
                                                    {errors.SO_NUMBER && touched.SO_NUMBER && (
                                                        <Form.Text className="text-danger">
                                                            {errors.SO_NUMBER}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Invoice Number *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="text"
                                                        name="INVOICE_NUMBER"
                                                        placeholder="Enter Invoice Number"
                                                        isInvalid={touched.INVOICE_NUMBER && errors.INVOICE_NUMBER}
                                                    />
                                                    {errors.INVOICE_NUMBER && touched.INVOICE_NUMBER && (
                                                        <Form.Text className="text-danger">
                                                            {errors.INVOICE_NUMBER}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Invoice Date *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="date"
                                                        name="INVOICE_DATE"
                                                        value={values.INVOICE_DATE || ''}
                                                        //onChange={(e) => setFieldValue('INVOICE_DATE', e.target.value)}
                                                        isInvalid={touched.INVOICE_DATE && errors.INVOICE_DATE}
                                                    />
                                                    {errors.INVOICE_DATE && touched.INVOICE_DATE && (
                                                        <Form.Text className="text-danger">
                                                            {errors.INVOICE_DATE}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Courier Name *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="text"
                                                        name="COURIER_NAME"
                                                        placeholder="Enter Courier Name"
                                                        isInvalid={touched.COURIER_NAME && errors.COURIER_NAME}
                                                    />
                                                    {errors.COURIER_NAME && touched.COURIER_NAME && (
                                                        <Form.Text className="text-danger">
                                                            {errors.COURIER_NAME}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>LR Number *</Form.Label>
                                                    <Field
                                                        as={Form.Control}
                                                        type="text"
                                                        name="LR_NUMBER"
                                                        placeholder="Enter LR Number"
                                                        isInvalid={touched.LR_NUMBER && errors.LR_NUMBER}
                                                    />
                                                    {errors.LR_NUMBER && touched.LR_NUMBER && (
                                                        <Form.Text className="text-danger">
                                                            {errors.LR_NUMBER}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Commercial User Fields for PENDING Status - Only comments required */}
                            {IsCommercialApproval() && values.ACTION === 'PENDING' && (
                                <Alert variant="warning">
                                    <strong>Note:</strong> When marking as Pending, please provide detailed comments explaining what information is missing or required.
                                </Alert>
                            )}

                            {/* Current Approver Info */}
                            {currentApprover && (
                                <Alert variant="info">
                                    <strong>Approving as:</strong> {currentApprover}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={onHide}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSubmitting || !values.ACTION}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Action'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

// Main View Details Component
const ServiceRequestView = ({ isApproverView = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [currentApprover, setCurrentApprover] = useState(sessionStorage.getItem('userrole'));
    const [approverComments, setApproverComments] = useState(null);

    useEffect(() => {
        loadRequestDetails();
    }, [id]);

    const loadRequestDetails = async () => {
        try {
            const response = await adminInstance.get(`servicerequest/GetDetails/${id}`);
            setRequest(response.data);
        } catch (err) {
            setError('Failed to load request details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprovalSubmit = async (approvalData) => {
        try {
            setLoading(true);
            const response = await adminInstance.post('servicerequest/UpdateStatus', approvalData);
            alert('Saved successfully!');
            navigate('/ServiceRequestApprovalList');
        } catch (err) {
            alert('Failed to submit action');
            console.error(err);
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status] || { label: status, color: 'secondary' };
        return <Badge bg={config.color}>{config.label}</Badge>;
    };

    const getActionBadge = (action) => {
        const colors = {
            APPROVED: 'success',
            Approved: 'success',
            REJECTED: 'danger',
            Rejected: 'danger',
            PENDING: 'secondary',
            Pending: 'secondary'
        };
        return <Badge bg={colors[action] || 'secondary'}>{action}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not yet';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading request details...</p>
            </Container>
        );
    }

    if (error || !request) {
        return (            
            <Container className="mt-4">
                <Alert variant="danger">
                    {error || 'Request not found'}
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
                    </Container>

        );
    }

    return (
        <>
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card aon-card">
                <div className="card-header aon-card-header">
                    <div className="d-flex justify-content-between align-items-start">

                        {/* Left side: Title + badges */}
                        <div>
                            <h4 className="mb-2">
                                Service Request #{request.ID}
                            </h4>

                            <div
                                className="d-flex align-items-center gap-3"
                                style={{ fontSize: '1.1rem' }}
                            >
                                <span className="text-muted">
                                    #{request.ID}
                                </span>
                                <Badge
                                    bg={SERVICE_TYPES[request.REQ_TYPE]?.color || 'primary'}
                                    className="px-3 py-2"
                                >
                                    {SERVICE_TYPES[request.REQ_TYPE]?.label}
                                </Badge>

                                {getStatusBadge(request.REQ_STATUS)}

                                <span className="text-muted">
                                    <strong> Total: ₹{request.TOTAL_AMOUNT?.toFixed(2)}</strong>
                                </span>
                            </div>
                        </div>

                        <div>
                        {/* Right side: Back button */}
                        <Button
                                variant="outline-secondary"
                                onClick={() => isApproverView ? navigate('/ServiceRequestApprovalList') : navigate('/MyServiceRequest')}
                        >
                            Back to List
                        </Button> &nbsp;
                            {isApproverView && (
                            <Button
                                variant="primary"
                                onClick={() => setShowActionModal(true)}
                                disabled={request.REQ_STATUS === 'COMPLETED'}
                            >
                                Take Action
                            </Button>
                            )}
                        </div>
                    </div>
                </div>

            <div className="card-body aon-card-body">
                <Container fluid className="py-1">           

            {/* Basic Details Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">Request Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Request Date:</strong> {formatDate(request.REQ_DATE)}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Customer:</strong> {request.CUS_CODE} | {request.CUS_NAME}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Address:</strong> {request.CUS_ADDRESS}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Employee :</strong> {request.EMP_ID} | {request.EMP_NAME} 
                                </ListGroup.Item>                                
                            </ListGroup>
                        </Col>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Product: {request.ITEM_CODE} | {request.ITEM_DESC}</strong>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Model:</strong> {request.MODEL}
                                </ListGroup.Item>
                                {request.SERIAL_NO && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>Serial Number:</strong> {request.SERIAL_NO}
                                    </ListGroup.Item>
                                )}
                                {request.PO_REFERENCE && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>PO Reference:</strong> {request.PO_REFERENCE}
                                    </ListGroup.Item>
                                )}
                                {request.ATTEND_DATE && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>Attend Date:</strong> {new Date(request.ATTEND_DATE).toLocaleDateString()}
                                    </ListGroup.Item>
                                )}
                                {request.INSTALLATION_DATE && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>Installation Date:</strong> {new Date(request.INSTALLATION_DATE).toLocaleDateString()}
                                    </ListGroup.Item>
                                )}
                            </ListGroup>
                        </Col>
                    </Row>
                </Card.Body>
                        </Card>

            {/* Invoice Details Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">Invoice Details</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>SO Number:</strong> {request.SO_NO}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                                <strong>Invoice No & Date:</strong> {request.INV_NO} | {formatDate(request.INV_DATE)}
                                </ListGroup.Item>                                
                            </ListGroup>
                        </Col>
                        <Col md={6}>
                            <ListGroup variant="flush">

                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Courier Name: {request.COURIER_NAME}</strong>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>LR Number:</strong> {request.LR_NO}
                                </ListGroup.Item>                                
                            </ListGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Description/Notes Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">
                        {request.serviceType === 'SERVICE' ? 'Notes' : 'Notes'}
                    </h5>
                </Card.Header>
                <Card.Body>
                    <p className="mb-0">{request.NOTE}</p>
                </Card.Body>
            </Card>

            {/* Spare Items Section (if exists) */}
            {request.ITEMS && request.ITEMS.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Spare Items</h5>
                        <Badge bg="info" pill>
                            Total: ₹{request.TOTAL_AMOUNT?.toFixed(2)}
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>SL No</th>
                                        <th>Item Code</th>
                                        <th>Description</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-center">Quantity</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.ITEMS.map((item, index) => (
                                        <tr key={item.ID}>
                                            <td>{index + 1}</td>
                                            <td>{item.ITEM_CODE}</td>
                                            <td>{item.ITEM_DESC}</td>
                                            <td className="text-end">₹{item.UNIT_PRICE?.toFixed(2)}</td>
                                            <td className="text-center">{item.QTY}</td>
                                            <td className="text-end">₹{item.AMOUNT?.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="table-active">
                                        <td colSpan={5} className="text-end fw-bold">Total Amount:</td>
                                        <td className="text-end fw-bold">
                                            ₹{request.TOTAL_AMOUNT?.toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Attachments Section */}
            {request.REQ_FILES && request.REQ_FILES.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">Attachments</h5>
                    </Card.Header>
                    <Card.Body>
                        <ListGroup horizontal>
                            {request.REQ_FILES.map((file, index) => (
                                <ListGroup.Item key={index}>
                                    <Button
                                        as="a"
                                        href={file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="outline-primary"
                                        size="sm"
                                    >
                                        <i className="bi bi-file-earmark me-2"></i>
                                        File {index + 1}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card.Body>
                </Card>
            )}

            {/* Approval Workflow Section */}
            <Card className="mb-4">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Approval Workflow</h5>
                    <Badge bg="secondary">
                        {request.APPROVERS?.filter(a => a.Action === 'APPROVED').length || 0} / {request.APPROVERS?.length || 0} Approved
                    </Badge>
                </Card.Header>
                <Card.Body>
                    {request.APPROVERS && request.APPROVERS.length > 0 ? (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th width="10%">Step</th>
                                        <th width="20%">Role</th>
                                        <th width="15%">Status</th>
                                        <th width="20%">Action By</th>
                                        <th width="20%">Date & Time</th>
                                        <th width="25%">Comments</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.APPROVERS.map((approval, index) => (
                                        <tr key={approval.ID} className={(approval.ACTION !== 'Approved') ? 'table-warning' : ''}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>
                                                <strong>{approval.Role}</strong>
                                            </td>
                                            <td>{getActionBadge(approval.Action)}</td>
                                            <td>
                                                {approval.By || (
                                                    <span className="text-muted"></span>
                                                )}
                                            </td>
                                            <td>{approval.On ? formatDate(approval.On) : "--"}</td>
                                            <td>
                                                {approval.Comment ? (
                                                    <div className="text-truncate" title={approval.Comment}>
                                                        {approval.Comment}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">No comments</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <Alert variant="info" className="mb-0">
                            No approval workflow defined for this request type.
                        </Alert>
                    )}

                    {/* Approver Action (for approver view) */}
                    {isApproverView && currentApprover && (
                                    <Accordion className="mt-4" activeKey="0">
                                        <Accordion.Item eventKey="0" >
                                <Accordion.Header>
                                    <strong>Take Action</strong>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Alert variant="info" className="mb-3">
                                        You are approving as  <strong>{currentApprover}</strong> 
                                    </Alert>


                                    {/*<Form.Group className="mb-3">*/}
                                    {/*    <Form.Label>Comments</Form.Label>*/}
                                    {/*    <Field*/}
                                    {/*        as={Form.Control}*/}
                                    {/*        name="COMMENTS"*/}
                                    {/*        as="textarea"*/}
                                    {/*        rows={3}*/}
                                    {/*        placeholder="Enter your comments..."*/}
                                    {/*        onChange={(e) => setApproverComments(e.target.value)}*/}
                                    {/*    />*/}
                                    {/*</Form.Group>*/}

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            onClick={() => setShowActionModal(true)}
                                        >
                                            <i className="bi bi-check-circle me-2"></i>
                                            Take Action
                                        </Button>
                                        {/*<Button*/}
                                        {/*    variant="danger"*/}
                                        {/*    onClick={() => {*/}
                                        {/*        //setFieldValue('ACTION', 'REJECTED');*/}
                                        {/*        setShowActionModal(true);*/}
                                        {/*    }}*/}
                                        {/*>*/}
                                        {/*    <i className="bi bi-x-circle me-2"></i>*/}
                                        {/*    Reject*/}
                                        {/*</Button>*/}
                                        {/*<Button*/}
                                        {/*    variant="warning"*/}
                                        {/*    onClick={() => {*/}
                                        {/*       // setFieldValue('ACTION', 'REQUEST_CHANGES');*/}
                                        {/*        setShowActionModal(true);*/}
                                        {/*    }}*/}
                                        {/*>*/}
                                        {/*    <i className="bi bi-arrow-clockwise me-2"></i>*/}
                                        {/*    Request Changes*/}
                                        {/*</Button>*/}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </Card.Body>
            </Card>

            {/* Approver Action Modal */}
            <ApproverActionModal
                            show={showActionModal}
                            onHide={() => setShowActionModal(false)}
                            onSubmit={handleApprovalSubmit}
                            requestId={request.ID}
                            currentApprover={currentApprover}
                            reqStatus={request.REQ_STATUS}
                            SoNo={request.SO_NO}
                            InvNo={request.INV_NO}
                            InvDate={request.INV_DATE}
                            CourierName={request.COURIER_NAME}
                            LrNumber={request.LR_NO}
                            CommercialComment={request.APPROVERS[3].Comment}
            />

            <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        
        .timeline-item {
          position: relative;
          margin-bottom: 20px;
        }
        
        .timeline-marker {
          position: absolute;
          left: -30px;
          top: 0;
        }
        
        .timeline-marker .marker {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 0 0 3px #dee2e6;
        }
        
        .timeline-content {
          padding: 10px;
          background: white;
          border-radius: 4px;
        }
        
        .timeline-item:not(:last-child) .timeline-content:after {
          content: '';
          position: absolute;
          left: -20px;
          top: 20px;
          width: 2px;
          height: calc(100% + 20px);
          background: #dee2e6;
        }
      `}</style>
                </Container>
            </div>
        </div>
        </>
    );
};

export default ServiceRequestView;
